import type { AxiosResponse } from "axios";
import axiosInstance from "../config/axios";
import purchaseService from "./purchaseService";
import {
  PurchaseOrderFormValues,
  PurchaseOrderOrganizationInfo,
  PurchaseOrderPayload,
  PurchaseOrderCreatePayload,
  PurchaseOrderListItem,
  PurchaseOrderListParams,
  PurchaseOrderListResponse,
  PurchaseOrderDetail,
  PurchaseOrderItemDetail,
  PurchaseOrderDocumentFile,
  PurchaseOrderChatMessage,
  PurchaseOrderTemplate,
  PurchaseOrderTemplatePayload,
  PurchaseOrderTemplateOption,
  PurchaseOrderVendorInfo,
  PurchaseOrderVendorSnapshot,
  PurchaseOrderTotals,
  PurchaseOrderRevisionHistory,
  PurchaseOrderRevisionRequestSummary,
  UnitOfMeasureOption,
  PurchaseOrderLookupOption,
} from "../types/purchaseOrder";

export interface PurchaseOrderAccess {
  readAccess: boolean;
  writeAccess: boolean;
  deleteAccess: boolean;
  approveAccess: boolean;
  reviewAccess: boolean;
}

export interface PurchaseExpenseCodeRow {
  expenseId: string;
  expenseCode: string;
  description?: string | null;
  isApproved?: boolean | null;
  createdAt?: string | null;
}

export interface PurchaseClassificationCode {
  id: string;
  code: string;
  description?: string | null;
  gstRate?: number | string | null;
}

export interface PurchaseClassificationCodeResponse {
  success?: boolean;
  data?: PurchaseClassificationCode[];
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
    hasNext?: boolean;
    hasPrev?: boolean;
  };
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  alreadyExists?: boolean;
}

interface CreateLookupResponse<TOption> {
  option: TOption;
  message?: string;
  alreadyExists?: boolean;
}

interface PurchaseOrderListApiRow {
  id: string;
  po_number?: string | null;
  base_po_number?: string | null;
  revision_number?: number | string | null;
  revision_status?: number | string | null;
  job_no?: string | null;
  date?: string | null;
  status?: string | null;
  contact_person_name?: string | null;
  party_name?: string | null;
  created_at?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  updated_at?: string | null;
  updated_by_name?: string | null;
  updated_by_email?: string | null;
  latest_revision_request_id?: string | null;
  latest_revision_request_status?: string | null;
  latest_revision_request_remarks?: string | null;
  latest_revision_requested_at?: string | null;
  has_active_revision_request?: boolean | null;
  edit_locked?: boolean | null;
  edit_locked_at?: string | null;
  edit_unlocked_at?: string | null;
  edit_unlocked_by_name?: string | null;
  pdf_available?: boolean | null;
  acknowledged?: boolean | null;
  acknowledged_at?: string | null;
  acknowledged_by_name?: string | null;
  has_acknowledged_document?: boolean | null;
  has_assets?: boolean | null;
  is_manual_entry?: boolean | null;
}

interface PurchaseOrderListApiResponse {
  success?: boolean;
  data?: PurchaseOrderListApiRow[];
  pagination?: {
    page?: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
  };
}

interface PurchaseOrderTotalsApi {
  subTotal?: number | string | null;
  totalGST?: number | string | null;
  grandTotal?: number | string | null;
}

interface PurchaseOrderItemApi {
  id: string;
  serialNo?: number | string | null;
  itemName?: string | null;
  description?: string | null;
  itemDescription?: string | null;
  hsnCode?: string | null;
  uomId?: string | null;
  quantity?: number | string | null;
  unitRate?: number | string | null;
  totalPrice?: number | string | null;
  gstRate?: number | string | null;
  workShipDate?: string | null;
  requestDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface PurchaseOrderDetailApi {
  id: string;
  poNumber?: string | null;
  basePoNumber?: string | null;
  revisionNumber?: number | string | null;
  hasActiveRevisionRequest?: boolean;
  latestRevisionRequest?: {
    id?: string;
    status?: string | null;
    remarks?: string | null;
    requestedAt?: string | null;
    attachmentPath?: string | null;
    attachmentOriginalName?: string | null;
  } | null;
  jobNumber?: string | null;
  orderDate?: string | null;
  status?: string | null;
  partyId?: string | null;
  templateId?: number | string | null;
  partyCodeId?: string | null;
  transporter?: string | null;
  modeOfTransport?: string | null;
  deliveryInstruction?: string | null;
  paymentTerms?: string | null;
  notes?: string | null;
  deliveryAddress?: string | null;
  delivery?: string | null;
  penalty?: string | null;
  warranty?: string | null;
  insurance?: string | null;
  pAndF?: string | null;
  freight?: string | null;
  freightCharges?: number | string | null;
  freightGST?: number | string | null;
  totalFreight?: number | string | null;
  gstPercentage?: number | string | null;
  clientProvidedPoNumber?: string | null;
  totals?: PurchaseOrderTotalsApi | null;
  vendorSnapshot?: PurchaseOrderVendorSnapshot | null;
  vendor?: PurchaseOrderVendorInfo | null;
  metadata?: Record<string, unknown> | null;
  requestMetadata?: Record<string, unknown> | null;
  organizationId?: string | null;
  contactPersonId?: string | null;
  contactPersonNumber?: string | null;
  contactPersonName?: string | null;
  project?: { code?: string | null; name?: string | null } | null;
  expense?: { id?: string | null; code?: string | null; description?: string | null } | null;
  beneficiaryPartyCodeId?: string | null;
  beneficiaryPartyCode?: { id?: string | null; code?: string | null; name?: string | null } | null;
  notesData?: Record<string, unknown> | null;
  createdAt?: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  updatedByEmail?: string | null;
  items?: PurchaseOrderItemApi[];
  editLocked?: boolean | null;
  editLockedAt?: string | null;
  editLockedBy?: string | null;
  editLockedByName?: string | null;
  editUnlockedAt?: string | null;
  editUnlockedBy?: string | null;
  editUnlockedByName?: string | null;
}

export interface PurchaseStaffEmployeeOption {
  id: string;
  name: string;
}

export interface PurchaseStaffEmployeeDetail extends PurchaseStaffEmployeeOption {
  email?: string | null;
  number?: string | null;
}

interface PurchaseOrderRevisionHistoryApi {
  success?: boolean;
  data?: {
    purchaseOrderId: string;
    currentPoNumber?: string | null;
    currentRevisionNumber?: number | string | null;
    revisions?: Array<{
      id: string;
      revisionNumber?: number | string | null;
      poNumber?: string | null;
      snapshot?: Record<string, unknown>;
      remarks?: string | null;
      attachmentPath?: string | null;
      attachmentOriginalName?: string | null;
      createdAt?: string | null;
      createdBy?: string | null;
      createdByName?: string | null;
      createdByEmail?: string | null;
      revisionRequestStatus?: string | null;
      revisionRequestRemarks?: string | null;
      revisionRequestedAt?: string | null;
      revisionResolvedAt?: string | null;
    }>;
  };
}

interface PurchaseOrderDocumentApiRow {
  id?: number | string;
  po_document_id?: number | string;
  poDocumentId?: number | string;
  document_id?: number | string;
  documentId?: number | string;
  revision?: number | string | null;
  filename?: string | null;
  original_filename?: string | null;
  originalFilename?: string | null;
  file_extension?: string | null;
  fileExtension?: string | null;
  filepath?: string | null;
  filesize?: number | string | null;
  fileSize?: number | string | null;
  filemimetype?: string | null;
  fileMimeType?: string | null;
  mimeType?: string | null;
  object_name?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  created_by?: string | null;
  createdBy?: string | null;
  uploaded_by_name?: string | null;
  uploadedByName?: string | null;
}

interface PurchaseOrderChatMessageApiRow {
  id: string;
  purchase_order_id?: string | number;
  purchaseOrderId?: string | number;
  message?: string | null;
  created_at?: string | null;
  createdAt?: string | null;
  created_by?: string | null;
  createdBy?: string | null;
  created_by_name?: string | null;
  createdByName?: string | null;
  created_by_email?: string | null;
  createdByEmail?: string | null;
}

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumberOrZero = (value: unknown): number => toNullableNumber(value) ?? 0;

interface PurchaseExpenseCodeResponse {
  success?: boolean;
  data?: PurchaseExpenseCodeRow[];
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
  };
}

class PurchaseOrderService {
  private readonly baseRoute = "/purchase/purchase-orders";

  async getAccess(): Promise<PurchaseOrderAccess> {
    const { data } = await axiosInstance.get<{
      success?: boolean;
      data: PurchaseOrderAccess;
      message?: string;
    }>("/purchase/access");

    if (!data?.success) {
      throw new Error(data?.message ?? "Failed to load purchase order access.");
    }

    return data.data;
  }

  private mapDocumentRow(row: PurchaseOrderDocumentApiRow): PurchaseOrderDocumentFile {
    const parseNumber = (value: unknown): number | null => {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const id = parseNumber(row.id) ?? 0;
    const poDocumentId =
      parseNumber(row.po_document_id ?? row.poDocumentId ?? row.document_id ?? row.documentId) ?? 0;

    return {
      id,
      poDocumentId,
      revision: toNullableNumber(row.revision) ?? null,
      filename: row.filename ?? null,
      originalFilename: row.original_filename ?? row.originalFilename ?? null,
      fileExtension: row.file_extension ?? row.fileExtension ?? null,
      fileSize: parseNumber(row.filesize ?? row.fileSize) ?? null,
      mimeType: row.filemimetype ?? row.fileMimeType ?? row.mimeType ?? null,
      createdAt: row.created_at ?? row.createdAt ?? null,
      createdBy: row.created_by ?? row.createdBy ?? undefined,
      uploadedByName: row.uploaded_by_name ?? row.uploadedByName ?? undefined,
    };
  }

  private mapChatMessageRow(row: PurchaseOrderChatMessageApiRow): PurchaseOrderChatMessage {
    return {
      id: row.id,
      purchaseOrderId: row.purchase_order_id ?? row.purchaseOrderId ?? "",
      message: row.message ?? "",
      createdAt: row.created_at ?? row.createdAt ?? null,
      createdBy: row.created_by ?? row.createdBy ?? null,
      createdByName: row.created_by_name ?? row.createdByName ?? null,
      createdByEmail: row.created_by_email ?? row.createdByEmail ?? null,
    };
  }

  async getOrganizationProfile(): Promise<PurchaseOrderOrganizationInfo> {
    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderOrganizationInfo>
    >(`/purchase/organization-profile`);

    if (!response.data?.data) {
      throw new Error("Organization profile response malformed");
    }

    return response.data.data;
  }

  async getTemplates(): Promise<PurchaseOrderTemplate[]> {
    const response = await axiosInstance.get<ApiResponse<PurchaseOrderTemplate[]>>(
      `${this.baseRoute}/templates`
    );

    return response.data?.data ?? [];
  }

  async getTemplateOptions(): Promise<PurchaseOrderTemplateOption[]> {
    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderTemplateOption[]>
    >(`${this.baseRoute}/template-options`);

    return response.data?.data ?? [];
  }

  async getTemplateInfo<T = unknown>(templateId: string): Promise<T | null> {
    const response = await axiosInstance.get<
      ApiResponse<{ id: string; templateId: string; jsonData: T } | null>
    >(`${this.baseRoute}/template-options/${templateId}/info`);

    const payload = response.data?.data;
    if (!payload) {
      return null;
    }

    return payload.jsonData;
  }

  async createTemplate(
    payload: PurchaseOrderTemplatePayload
  ): Promise<PurchaseOrderTemplate> {
    const response = await axiosInstance.post<
      ApiResponse<PurchaseOrderTemplate>
    >(`${this.baseRoute}/templates`, payload);

    if (!response.data?.data) {
      throw new Error("Template create response malformed");
    }

    return response.data.data;
  }

  async updateTemplate(
    id: string,
    payload: PurchaseOrderTemplatePayload
  ): Promise<PurchaseOrderTemplate> {
    const response = await axiosInstance.put<
      ApiResponse<PurchaseOrderTemplate>
    >(`${this.baseRoute}/templates/${id}`, payload);

    if (!response.data?.data) {
      throw new Error("Template update response malformed");
    }

    return response.data.data;
  }

  async deleteTemplate(id: string): Promise<void> {
    await axiosInstance.delete(`${this.baseRoute}/templates/${id}`);
  }

  async getUnitsOfMeasure(): Promise<UnitOfMeasureOption[]> {
    const response = await axiosInstance.get<
      ApiResponse<UnitOfMeasureOption[]>
    >(`${this.baseRoute}/unit-of-measure`);

    return response.data?.data ?? [];
  }

  async createUnitOfMeasure(name: string): Promise<CreateLookupResponse<UnitOfMeasureOption>> {
    const response = await axiosInstance.post<
      ApiResponse<UnitOfMeasureOption>
    >(`${this.baseRoute}/unit-of-measure`, { name });

    if (!response.data?.data) {
      throw new Error("Unit of measure response malformed");
    }

    return {
      option: response.data.data,
      message: response.data.message,
      alreadyExists: response.data.alreadyExists,
    };
  }

  async getModeOfTransportOptions(): Promise<PurchaseOrderLookupOption[]> {
    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderLookupOption[]>
    >(`${this.baseRoute}/mode-of-transports`);

    return response.data?.data ?? [];
  }

  async createModeOfTransport(name: string): Promise<
    CreateLookupResponse<PurchaseOrderLookupOption>
  > {
    const response = await axiosInstance.post<
      ApiResponse<PurchaseOrderLookupOption>
    >(`${this.baseRoute}/mode-of-transports`, { name });

    if (!response.data?.data) {
      throw new Error("Mode of transport response malformed");
    }

    return {
      option: response.data.data,
      message: response.data.message,
      alreadyExists: response.data.alreadyExists,
    };
  }

  async getPaymentTermOptions(): Promise<PurchaseOrderLookupOption[]> {
    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderLookupOption[]>
    >(`${this.baseRoute}/payment-terms`);

    return response.data?.data ?? [];
  }

  async createPaymentTerm(name: string): Promise<
    CreateLookupResponse<PurchaseOrderLookupOption>
  > {
    const response = await axiosInstance.post<
      ApiResponse<PurchaseOrderLookupOption>
    >(`${this.baseRoute}/payment-terms`, { name });

    if (!response.data?.data) {
      throw new Error("Payment term response malformed");
    }

    return {
      option: response.data.data,
      message: response.data.message,
      alreadyExists: response.data.alreadyExists,
    };
  }

  async getPurchaseStaffEmployees(): Promise<PurchaseStaffEmployeeOption[]> {
    const response = await axiosInstance.get<{
      success?: boolean;
      data?: PurchaseStaffEmployeeOption[];
    }>(`${this.baseRoute}/staff-employees`);

    if (Array.isArray(response.data?.data)) {
      return response.data.data;
    }

    return [];
  }

  async getPurchaseStaffEmployeeDetails(
    staffEmployeeId: string
  ): Promise<PurchaseStaffEmployeeDetail | null> {
    if (!staffEmployeeId) {
      return null;
    }

    const response = await axiosInstance.get<{
      success?: boolean;
      data?: PurchaseStaffEmployeeDetail;
    }>(`${this.baseRoute}/staff-employees/${staffEmployeeId}`);

    return response.data?.data ?? null;
  }

  async fetchPurchaseOrderPdf(
    orderId: string,
    mode: "preview" | "download"
  ): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get(`${this.baseRoute}/${orderId}/pdf/${mode}`, {
      responseType: "blob",
    });
  }

  async getPurchaseOrdersClientwise(
    params: PurchaseOrderListParams
  ): Promise<PurchaseOrderListResponse> {
    const {
      page = 1,
      pageSize = 20,
      search,
      projectCodeId,
      expenseCodeId,
      partyCodeId,
      dateFrom,
      dateTo,
      acknowledgedOnly,
    } = params;

    const response = await axiosInstance.get<PurchaseOrderListApiResponse>(
      `/purchase/purchase-orders`,
      {
        params: {
          page,
          pageSize,
          ...(typeof search === "string" && search.trim().length > 0
            ? { search: search.trim() }
            : {}),
          ...(projectCodeId ? { projectCodeId } : {}),
          ...(expenseCodeId ? { expenseCodeId } : {}),
          ...(partyCodeId ? { partyCodeId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(acknowledgedOnly ? { acknowledgedOnly: "true" } : {}),
        },
      }
    );

    const payload = response.data ?? {};
    const rows = Array.isArray(payload.data) ? payload.data : [];

    const data: PurchaseOrderListItem[] = rows.map((row) => ({
      id: row.id,
      poNumber: row.po_number ?? null,
      basePoNumber: row.base_po_number ?? null,
      revisionNumber: toNullableNumber(row.revision_number),
      revisionStatus: toNullableNumber(row.revision_status),
      hasActiveRevisionRequest: Boolean(row.has_active_revision_request),
      latestRevisionRequest: row.latest_revision_request_id
        ? {
            id: row.latest_revision_request_id,
            status: row.latest_revision_request_status ?? null,
            remarks: row.latest_revision_request_remarks ?? null,
            requestedAt: row.latest_revision_requested_at ?? null,
          }
        : null,
      jobNumber: row.job_no ?? null,
      orderDate: row.date ?? null,
      status: row.status ?? null,
      contactPersonName: row.contact_person_name ?? null,
      partyName: row.party_name ?? null,
      createdAt: row.created_at ?? null,
      createdByName: row.created_by_name ?? null,
      createdByEmail: row.created_by_email ?? null,
      updatedAt: row.updated_at ?? null,
      updatedByName: row.updated_by_name ?? null,
      updatedByEmail: row.updated_by_email ?? null,
      editLocked: row.edit_locked ?? false,
      editLockedAt: row.edit_locked_at ?? null,
      editUnlockedAt: row.edit_unlocked_at ?? null,
      editUnlockedByName: row.edit_unlocked_by_name ?? null,
      pdfAvailable: Boolean(row.pdf_available),
      acknowledged: Boolean(row.acknowledged),
      acknowledgedAt: row.acknowledged_at ?? null,
      acknowledgedByName: row.acknowledged_by_name ?? null,
      hasAcknowledgedDocument: Boolean(row.has_acknowledged_document),
      hasAssets: Boolean(row.has_assets),
      isManualEntry: Boolean(row.is_manual_entry),
    }));

    const total = payload.pagination?.total ?? data.length;
    const rawPageSize = payload.pagination?.pageSize ?? pageSize;
    const safePageSize = rawPageSize > 0 ? rawPageSize : pageSize;
    const totalPages = safePageSize > 0 ? Math.ceil(total / safePageSize) : 0;

    return {
      success: payload.success !== false,
      data,
      pagination: {
        page: payload.pagination?.page ?? page,
        pageSize: safePageSize,
        total,
        totalPages,
      },
    };
  }

  async exportPurchaseOrdersToExcel(params: {
    search?: string;
    projectCodeId?: string;
    expenseCodeId?: string;
    partyCodeId?: string;
    dateFrom?: string;
    dateTo?: string;
    columns?: string[];
    revisionType?: "all" | "po_with_revisions" | "acknowledged_pos";
  }): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get(`/purchase/purchase-orders/export-excel`, {
      responseType: "blob",
      params: {
        ...(params.search ? { search: params.search } : {}),
        ...(params.projectCodeId ? { projectCodeId: params.projectCodeId } : {}),
        ...(params.expenseCodeId ? { expenseCodeId: params.expenseCodeId } : {}),
        ...(params.partyCodeId ? { partyCodeId: params.partyCodeId } : {}),
        ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        ...(Array.isArray(params.columns) && params.columns.length > 0
          ? { columns: params.columns.join(",") }
          : {}),
        ...(params.revisionType
          ? {
              revisionType: params.revisionType,
              exportType: params.revisionType,
            }
          : {}),
      },
    });
  }

  async checkPoNumberExists(
    poNumber: string
  ): Promise<{ exists: boolean; orderId?: string }> {
    const response = await axiosInstance.get<
      ApiResponse<{ exists: boolean; orderId?: string }>
    >(`${this.baseRoute}/check-po-number`, {
      params: { poNumber },
    });
    return response.data?.data ?? { exists: false };
  }

  async createManualPurchaseOrder(
    payload: PurchaseOrderCreatePayload
  ): Promise<ApiResponse<{ id: string; poNumber?: string }>> {
    const response = await axiosInstance.post<
      ApiResponse<{ id: string; poNumber?: string }>
    >(`${this.baseRoute}/manual`, payload);
    return response.data;
  }

  async createPurchaseOrder(
    payload: PurchaseOrderCreatePayload
  ): Promise<ApiResponse<{ id: string; poNumber?: string }>> {
    const response = await axiosInstance.post<
      ApiResponse<{ id: string; poNumber?: string }>
    >(this.baseRoute, payload);
    return response.data;
  }

  async updatePurchaseOrder(
    id: string,
    payload: PurchaseOrderCreatePayload
  ): Promise<ApiResponse<{ id: string; poNumber?: string }>> {
    const response = await axiosInstance.put<
      ApiResponse<{ id: string; poNumber?: string }>
    >(`${this.baseRoute}/${id}`, payload);

    return response.data;
  }

  async approvePurchaseOrder(
    id: string
  ): Promise<ApiResponse<{ id: string; status?: string }>> {
    const response = await axiosInstance.post<
      ApiResponse<{ id: string; status?: string }>
    >(`${this.baseRoute}/${id}/approve`, {});

    return response.data;
  }

  async reviewPurchaseOrder(orderId: string): Promise<void> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to review an order");
    }

    await axiosInstance.post(`${this.baseRoute}/review`, {
      purchaseOrderId: orderId,
    });
  }

  async unlockPurchaseOrder(
    id: string
  ): Promise<
    ApiResponse<{
      id: string;
      editLocked: boolean;
      editUnlockedBy?: string | null;
      editUnlockedByName?: string | null;
      editUnlockedAt?: string | null;
    }>
  > {
    const response = await axiosInstance.post<
      ApiResponse<{
        id: string;
        editLocked: boolean;
        editUnlockedBy?: string | null;
        editUnlockedByName?: string | null;
        editUnlockedAt?: string | null;
      }>
    >(`${this.baseRoute}/${id}/unlock`, {});

    return response.data;
  }

  async requestPurchaseOrderRevision(
    id: string,
    payload: { remarks?: string; attachment?: File | null }
  ): Promise<ApiResponse<{ id: string; status?: string }>> {
    const formData = new FormData();
    if (payload.remarks) {
      formData.append("remarks", payload.remarks);
    }
    if (payload.attachment) {
      formData.append("attachment", payload.attachment);
    }

    const response = await axiosInstance.post<
      ApiResponse<{ id: string; status?: string }>
    >(`${this.baseRoute}/${id}/revision-requests`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return response.data;
  }

  async raisePurchaseRevisionRequest(orderId: string): Promise<void> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to raise a revision request");
    }

    await axiosInstance.post(`${this.baseRoute}/revision-request/raise`, {
      purchaseOrderId: orderId,
    });
  }

  async requestPurchaseOrderEditAccess(orderId: string): Promise<void> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to request edit access");
    }

    await axiosInstance.post(`${this.baseRoute}/${orderId}/request-edit-access`, {});
  }

  async approvePurchaseRevisionRequest(orderId: string): Promise<void> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to approve a revision request");
    }

    await axiosInstance.post(`${this.baseRoute}/revision-request/approve`, {
      purchaseOrderId: orderId,
    });
  }

  async approvePurchaseOrderRevisionRequest(
    id: string,
    requestId: string
  ): Promise<ApiResponse<{ id: string; status?: string }>> {
    const response = await axiosInstance.post<
      ApiResponse<{ id: string; status?: string }>
    >(`${this.baseRoute}/${id}/revision-requests/${requestId}/approve`, {});

    return response.data;
  }

  async rejectPurchaseOrderRevisionRequest(
    id: string,
    requestId: string,
    payload: { remarks?: string }
  ): Promise<ApiResponse<{ id: string; status?: string }>> {
    const response = await axiosInstance.post<
      ApiResponse<{ id: string; status?: string }>
    >(`${this.baseRoute}/${id}/revision-requests/${requestId}/reject`, payload ?? {});

    return response.data;
  }

  async getPurchaseOrderRevisionHistory(
    id: string
  ): Promise<PurchaseOrderRevisionHistory> {
    const response = await axiosInstance.get<PurchaseOrderRevisionHistoryApi>(
      `${this.baseRoute}/${id}/revisions`
    );

    const payload = response.data?.data;
    if (!payload) {
      throw new Error("Revision history response malformed");
    }

    const revisions = (payload.revisions ?? []).map((entry) => ({
      id: entry.id,
      revisionNumber: toNullableNumber(entry.revisionNumber) ?? 0,
      poNumber: entry.poNumber ?? "",
      snapshot: entry.snapshot ?? {},
      remarks: entry.remarks ?? null,
      attachmentPath: entry.attachmentPath ?? null,
      attachmentOriginalName: entry.attachmentOriginalName ?? null,
      createdAt: entry.createdAt ?? null,
      createdBy: entry.createdBy ?? null,
      createdByName: entry.createdByName ?? null,
      createdByEmail: entry.createdByEmail ?? null,
      revisionRequestStatus: entry.revisionRequestStatus ?? null,
      revisionRequestRemarks: entry.revisionRequestRemarks ?? null,
      revisionRequestedAt: entry.revisionRequestedAt ?? null,
      revisionResolvedAt: entry.revisionResolvedAt ?? null,
    }));

    return {
      purchaseOrderId: payload.purchaseOrderId,
      currentPoNumber: payload.currentPoNumber ?? null,
      currentRevisionNumber: toNullableNumber(payload.currentRevisionNumber) ?? 0,
      revisions,
    };
  }

  async downloadPurchaseOrderRevisionAttachment(
    orderId: string,
    revisionId: string
  ): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get(
      `${this.baseRoute}/${orderId}/revisions/${revisionId}/attachment`,
      { responseType: "blob" }
    );
  }

  async listPurchaseOrderDocuments(orderId: string): Promise<PurchaseOrderDocumentFile[]> {
    if (!orderId) {
      return [];
    }

    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderDocumentApiRow[]>
    >(`${this.baseRoute}/${orderId}/documents`);

    const rows = Array.isArray(response.data?.data) ? response.data?.data : [];
    return rows.map((row) => this.mapDocumentRow(row));
  }

  async uploadPurchaseOrderDocument(orderId: string, file: File): Promise<void> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to upload document");
    }

    const formData = new FormData();
    formData.append("file", file);

    await axiosInstance.post<ApiResponse<unknown>>(
      `${this.baseRoute}/${orderId}/documents`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  }

  async fetchPurchaseOrderDocument(
    orderId: string,
    fileId: string | number,
    mode: "preview" | "download"
  ): Promise<AxiosResponse<Blob>> {
    const targetMode = mode === "preview" ? "preview" : "download";
    return axiosInstance.get(
      `${this.baseRoute}/${orderId}/documents/${fileId}/${targetMode}`,
      { responseType: "blob" }
    );
  }

  async deletePurchaseOrderDocument(
    orderId: string,
    fileId: string | number
  ): Promise<void> {
    await axiosInstance.delete(`${this.baseRoute}/${orderId}/documents/${fileId}`);
  }

  async listPurchaseOrderChatMessages(
    orderId: string,
    options?: { limit?: number }
  ): Promise<PurchaseOrderChatMessage[]> {
    if (!orderId) {
      return [];
    }

    const config =
      options?.limit && options.limit > 0
        ? { params: { limit: options.limit } }
        : undefined;

    const response = await axiosInstance.get<
      ApiResponse<PurchaseOrderChatMessageApiRow[]>
    >(`${this.baseRoute}/${orderId}/chat/messages`, config);

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    return rows.map((row) => this.mapChatMessageRow(row));
  }

  async postPurchaseOrderChatMessage(
    orderId: string,
    message: string
  ): Promise<PurchaseOrderChatMessage> {
    if (!orderId) {
      throw new Error("Purchase order ID is required to send a chat message");
    }

    const response = await axiosInstance.post<
      ApiResponse<PurchaseOrderChatMessageApiRow>
    >(`${this.baseRoute}/${orderId}/chat/messages`, { message });

    const payload = response.data?.data;
    if (!payload) {
      throw new Error("Chat message response malformed");
    }

    return this.mapChatMessageRow(payload);
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrderDetail> {
    const response = await axiosInstance.get<ApiResponse<PurchaseOrderDetailApi>>(
      `${this.baseRoute}/${id}`
    );

    const payload = response.data?.data;
    if (!payload) {
      throw new Error("Purchase order details response malformed");
    }

    return this.transformPurchaseOrderDetail(payload);
  }

  async getVendorByPartyCode(
    partyCodeId: string
  ): Promise<PurchaseOrderVendorInfo> {
    try {
      const response = await axiosInstance.get<
        ApiResponse<PurchaseOrderVendorInfo>
      >(`${this.baseRoute}/vendors/${partyCodeId}`);

      if (response.data?.data) {
        return response.data.data;
      }
    } catch (error) {
      // Silently fallback to CodeVault API
      console.warn("PurchaseOrderService: falling back to CodeVault vendor lookup", error);
    }

    return this.getVendorFromCodeVault(partyCodeId);
  }

  private async getVendorFromCodeVault(
    partyCodeId: string
  ): Promise<PurchaseOrderVendorInfo> {
    const normalize = (partyData: any): PurchaseOrderVendorInfo => ({
      id: partyData?.id || partyCodeId,
      name: partyData?.company_name || partyData?.companyname || "",
      address: partyData?.address || partyData?.company_address,
      contactPersonName:
        partyData?.name_contact_person || partyData?.contact_person_name,
      contactEmail:
        partyData?.email_contact_person || partyData?.contact_person_email,
      contactPhone:
        partyData?.number_contact_person || partyData?.contact_person_phone,
      gstNumber: partyData?.company_gst_no,
      panNumber: partyData?.company_pan_no,
      cinNumber: partyData?.company_incorporation_number,
      bankName: partyData?.bank_name,
      bankAccountNumber: partyData?.bank_account_number,
      bankIfscCode: partyData?.bank_ifsc_code,
      bankMicrCode: partyData?.bank_micr_code,
    });

    const tryLookup = async (isApproved: boolean) => {
      const response = await purchaseService.getPartyDetails(
        partyCodeId,
        isApproved
      );
      if (response?.data) {
        return normalize(response.data);
      }
      return undefined;
    };

    const approved = await tryLookup(true);
    if (approved) {
      return approved;
    }

    const pending = await tryLookup(false);
    if (pending) {
      return pending;
    }

    throw new Error("Vendor not found for provided party code");
  }

  async getExpenseCodes(params: { page?: number; pageSize?: number; search?: string } = {}): Promise<PurchaseExpenseCodeResponse> {
    const { page = 1, pageSize = 10, search = "" } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await axiosInstance.get(`/purchase/expense-codes?${queryParams.toString()}`);
    return response.data;
  }

  async getHSNCodes(params: { page?: number; pageSize?: number; search?: string } = {}): Promise<PurchaseClassificationCodeResponse> {
    const { page = 1, pageSize = 20, search = "" } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await axiosInstance.get(`/purchase/hsn-codes?${queryParams.toString()}`);
    return response.data;
  }

  async getSACCodes(params: { page?: number; pageSize?: number; search?: string } = {}): Promise<PurchaseClassificationCodeResponse> {
    const { page = 1, pageSize = 20, search = "" } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await axiosInstance.get(`/purchase/sac-codes?${queryParams.toString()}`);
    return response.data;
  }

  mapFormToPayload(
    form: PurchaseOrderFormValues,
    organizationId: string
  ): PurchaseOrderPayload {
    const totals = this.calculateTotals(form.items ?? [], {
      freightCharges: form.freightCharges,
      freightGST: form.freightGST,
    });
    return {
      ...form,
      organizationId,
      totals,
    };
  }

  calculateTotals(
    items: PurchaseOrderFormValues["items"],
    freightData?: { freightCharges?: number; freightGST?: number }
  ): {
    subTotal: number;
    totalGST: number;
    grandTotal: number;
  } {
    const itemTotals = (items ?? []).reduce(
      (acc, item) => {
        const quantity = Number.isFinite(item.quantity) ? Number(item.quantity) : 0;
        const unitPrice = Number.isFinite(item.unitPrice) ? Number(item.unitPrice) : 0;
        const gstRate = Number.isFinite(item.gstRate) ? Number(item.gstRate) : 0;

        const rowTotal = quantity * unitPrice;
        const gstAmount = (rowTotal * gstRate) / 100;

        acc.subTotal += rowTotal;
        acc.totalGST += gstAmount;
        acc.grandTotal += rowTotal + gstAmount;

        return acc;
      },
      {
        subTotal: 0,
        totalGST: 0,
        grandTotal: 0,
      }
    );

    const freightChargesCandidate = freightData?.freightCharges;
    const freightCharges =
      typeof freightChargesCandidate === "number" && Number.isFinite(freightChargesCandidate)
        ? freightChargesCandidate
        : 0;

    const freightGstCandidate = freightData?.freightGST;
    const freightGstPercent =
      typeof freightGstCandidate === "number" && Number.isFinite(freightGstCandidate)
        ? freightGstCandidate
        : 0;

    const freightGstAmount = Number(((freightCharges * freightGstPercent) / 100).toFixed(2));
    const subTotal = Number(itemTotals.subTotal.toFixed(2));
    const totalGST = Number((itemTotals.totalGST + freightGstAmount).toFixed(2));
    const grandTotal = Number((subTotal + totalGST + freightCharges).toFixed(2));

    return {
      subTotal,
      totalGST,
      grandTotal,
    };
  }

  private transformPurchaseOrderDetail(payload: PurchaseOrderDetailApi): PurchaseOrderDetail {
    const totals: PurchaseOrderTotals | null = payload.totals
      ? {
          subTotal: toNumberOrZero(payload.totals.subTotal),
          totalGST: toNumberOrZero(payload.totals.totalGST),
          grandTotal: toNumberOrZero(payload.totals.grandTotal),
        }
      : null;

    const items: PurchaseOrderItemDetail[] = (payload.items ?? []).map((item: any) => ({
      id: item.id,
      serialNo: toNullableNumber(item.serialNo),
      itemName: item.itemName ?? item.description ?? null,
      description: item.itemDescription ?? item.description ?? null,
      hsnCode: item.hsnCode ?? null,
      uomId: item.uomId ?? null,
      quantity: toNullableNumber(item.quantity),
      unitRate: toNullableNumber(item.unitRate),
      totalPrice: toNullableNumber(item.totalPrice),
      gstRate: toNullableNumber(item.gstRate),
      workShipDate: item.workShipDate ?? null,
      requestDate: item.requestDate ?? null,
      isAsset: Boolean(item.isAsset),
      assetCategoryId:
        typeof item.assetCategoryId === "number" ? item.assetCategoryId : null,
      assetSerials: Array.isArray(item.assetSerials) ? item.assetSerials : null,
      createdAt: item.createdAt ?? null,
      updatedAt: item.updatedAt ?? null,
    }));

    const revisionNumber = toNullableNumber(payload.revisionNumber);
    const latestRevisionRequest: PurchaseOrderRevisionRequestSummary | null = payload.latestRevisionRequest
      ? {
          id: payload.latestRevisionRequest.id ?? "",
          status: payload.latestRevisionRequest.status ?? null,
          remarks: payload.latestRevisionRequest.remarks ?? null,
          requestedAt: payload.latestRevisionRequest.requestedAt ?? null,
          attachmentPath: payload.latestRevisionRequest.attachmentPath ?? null,
          attachmentOriginalName: payload.latestRevisionRequest.attachmentOriginalName ?? null,
        }
      : null;

    return {
      id: payload.id,
      poNumber: payload.poNumber ?? null,
      basePoNumber: payload.basePoNumber ?? null,
      revisionNumber,
      hasActiveRevisionRequest: Boolean(payload.hasActiveRevisionRequest),
      latestRevisionRequest,
      jobNumber: payload.jobNumber ?? null,
      orderDate: payload.orderDate ?? null,
      status: payload.status ?? null,
      partyId: payload.partyId ?? null,
      templateId: payload.templateId ?? null,
      partyCodeId: payload.partyCodeId ?? null,
      transporter: payload.transporter ?? null,
      modeOfTransport: payload.modeOfTransport ?? null,
      deliveryInstruction: payload.deliveryInstruction ?? null,
      paymentTerms: payload.paymentTerms ?? null,
      notes: payload.notes ?? null,
      deliveryAddress: payload.deliveryAddress ?? null,
      delivery: payload.delivery ?? null,
      penalty: payload.penalty ?? null,
      warranty: payload.warranty ?? null,
      insurance: payload.insurance ?? null,
      pAndF: payload.pAndF ?? null,
      freight: payload.freight ?? null,
      freightCharges: toNullableNumber(payload.freightCharges),
      freightGST: toNullableNumber(payload.freightGST),
      totalFreight: toNullableNumber(payload.totalFreight),
      gstPercentage: toNullableNumber(payload.gstPercentage),
      clientProvidedPoNumber: payload.clientProvidedPoNumber ?? null,
      totals,
      vendorSnapshot: payload.vendorSnapshot ?? null,
      vendor: payload.vendor ?? null,
      metadata: payload.metadata ?? null,
      requestMetadata: payload.requestMetadata ?? null,
      organizationId: payload.organizationId ?? null,
      contactPersonId: payload.contactPersonId ?? null,
      contactPersonNumber: payload.contactPersonNumber ?? null,
      contactPersonName: payload.contactPersonName ?? null,
      project: payload.project
        ? {
            code: payload.project.code ?? null,
            name: payload.project.name ?? null,
          }
        : null,
      expense: payload.expense
        ? {
            id: payload.expense.id ?? null,
            code: payload.expense.code ?? null,
            description: payload.expense.description ?? null,
          }
        : null,
      beneficiaryPartyCodeId: payload.beneficiaryPartyCodeId ?? null,
      beneficiaryPartyCode: payload.beneficiaryPartyCode
        ? {
            id: payload.beneficiaryPartyCode.id ?? null,
            code: payload.beneficiaryPartyCode.code ?? null,
            name: payload.beneficiaryPartyCode.name ?? null,
          }
        : null,
      notesData: payload.notesData ?? null,
      createdAt: payload.createdAt ?? null,
      createdByName: payload.createdByName ?? null,
      createdByEmail: payload.createdByEmail ?? null,
      updatedAt: payload.updatedAt ?? null,
      updatedByName: payload.updatedByName ?? null,
      updatedByEmail: payload.updatedByEmail ?? null,
      editLocked: payload.editLocked ?? false,
      editLockedAt: payload.editLockedAt ?? null,
      editLockedBy: payload.editLockedBy ?? null,
      editLockedByName: payload.editLockedByName ?? null,
      editUnlockedAt: payload.editUnlockedAt ?? null,
      editUnlockedBy: payload.editUnlockedBy ?? null,
      editUnlockedByName: payload.editUnlockedByName ?? null,
      items,
    };
  }

  // ============================================
  // PO Dispatch (Delivery Tracking) Methods
  // ============================================

  async getApprovedPurchaseOrders(params: { page?: number; pageSize?: number; search?: string } = {}): Promise<{
    data: ApprovedPurchaseOrder[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { page = 1, pageSize = 20, search = "" } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await axiosInstance.get(`/purchase/dispatches/approved-orders?${queryParams.toString()}`);
    return response.data;
  }

  async getPurchaseOrderItemsForDispatch(purchaseOrderId: number): Promise<{
    purchaseOrder: DispatchPurchaseOrderInfo;
    items: DispatchPurchaseOrderItem[];
  }> {
    const response = await axiosInstance.get(`/purchase/dispatches/orders/${purchaseOrderId}/items`);
    return response.data.data;
  }

  async createDispatch(payload: CreateDispatchPayload): Promise<{ success: boolean; dispatchId: number; message?: string }> {
    const response = await axiosInstance.post("/purchase/dispatches", payload);
    return response.data;
  }

  async listDispatches(params: { page?: number; pageSize?: number; purchaseOrderId?: number; status?: string } = {}): Promise<{
    data: DispatchListItem[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { page = 1, pageSize = 20, purchaseOrderId, status } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (purchaseOrderId) {
      queryParams.append("purchaseOrderId", String(purchaseOrderId));
    }
    if (status) {
      queryParams.append("status", status);
    }

    const response = await axiosInstance.get(`/purchase/dispatches?${queryParams.toString()}`);
    return response.data;
  }

  async getDispatchDetails(dispatchId: number): Promise<DispatchDetail> {
    const response = await axiosInstance.get(`/purchase/dispatches/${dispatchId}`);
    return response.data.data;
  }

  async updateDispatchItem(itemId: number, payload: { quantity?: number; expectedDeliveryDate?: string | null }): Promise<{ success: boolean; message?: string }> {
    const response = await axiosInstance.put(`/purchase/dispatches/items/${itemId}`, payload);
    return response.data;
  }

  async deleteDispatch(dispatchId: number): Promise<{ success: boolean; message?: string }> {
    const response = await axiosInstance.delete(`/purchase/dispatches/${dispatchId}`);
    return response.data;
  }

  async getPendingDeliveries(params: { page?: number; pageSize?: number; status?: string } = {}): Promise<{
    data: PendingDeliveryItem[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { page = 1, pageSize = 20, status } = params;
    const queryParams = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (status) {
      queryParams.append("status", status);
    }

    const response = await axiosInstance.get(`/purchase/deliveries/pending?${queryParams.toString()}`);
    return response.data;
  }

  async markItemsReceived(dispatchId: number, payload: MarkReceivedPayload): Promise<{ success: boolean; dispatchStatus: string; message?: string }> {
    const response = await axiosInstance.post(`/purchase/deliveries/${dispatchId}/receive`, payload);
    return response.data;
  }

  // ============================================
  // Stage-based Dispatch Methods (No PO Access)
  // For PurchaseStage and ExecutionStage
  // ============================================

  async getApprovedPurchaseOrdersByProject(params: { projectId: string; page?: number; pageSize?: number; search?: string }): Promise<{
    data: ApprovedPurchaseOrder[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { projectId, page = 1, pageSize = 20, search = "" } = params;
    const queryParams = new URLSearchParams({
      projectId,
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search.trim()) {
      queryParams.append("search", search.trim());
    }

    const response = await axiosInstance.get(`/purchase/stage-dispatches/approved-orders?${queryParams.toString()}`);
    return response.data;
  }

  async getPurchaseOrderItemsForStageDispatch(purchaseOrderId: number): Promise<{
    purchaseOrder: DispatchPurchaseOrderInfo;
    items: DispatchPurchaseOrderItem[];
  }> {
    const response = await axiosInstance.get(`/purchase/stage-dispatches/orders/${purchaseOrderId}/items`);
    return response.data.data;
  }

  async createStageDispatch(payload: CreateDispatchPayload): Promise<{ success: boolean; dispatchId: number; message?: string }> {
    const response = await axiosInstance.post("/purchase/stage-dispatches", payload);
    return response.data;
  }

  async listStageDispatches(params: { projectId: string; page?: number; pageSize?: number; purchaseOrderId?: number; status?: string }): Promise<{
    data: DispatchListItem[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { projectId, page = 1, pageSize = 20, purchaseOrderId, status } = params;
    const queryParams = new URLSearchParams({
      projectId,
      page: String(page),
      pageSize: String(pageSize),
    });
    if (purchaseOrderId) {
      queryParams.append("purchaseOrderId", String(purchaseOrderId));
    }
    if (status) {
      queryParams.append("status", status);
    }

    const response = await axiosInstance.get(`/purchase/stage-dispatches?${queryParams.toString()}`);
    return response.data;
  }

  async getStageDispatchDetails(dispatchId: number): Promise<DispatchDetail> {
    const response = await axiosInstance.get(`/purchase/stage-dispatches/${dispatchId}`);
    return response.data.data;
  }

  async deleteStageDispatch(dispatchId: number): Promise<{ success: boolean; message?: string }> {
    const response = await axiosInstance.delete(`/purchase/stage-dispatches/${dispatchId}`);
    return response.data;
  }

  async getPendingDeliveriesByProject(params: { projectId: string; page?: number; pageSize?: number; status?: string }): Promise<{
    data: PendingDeliveryItem[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { projectId, page = 1, pageSize = 20, status } = params;
    const queryParams = new URLSearchParams({
      projectId,
      page: String(page),
      pageSize: String(pageSize),
    });
    if (status) {
      queryParams.append("status", status);
    }

    const response = await axiosInstance.get(`/purchase/stage-deliveries/pending?${queryParams.toString()}`);
    return response.data;
  }

  async markItemsReceivedByProject(dispatchId: number, payload: MarkReceivedPayload): Promise<{ success: boolean; dispatchStatus: string; message?: string }> {
    const response = await axiosInstance.post(`/purchase/stage-deliveries/${dispatchId}/receive`, payload);
    return response.data;
  }

  // ── Acknowledged Document ────────────────────────────────────────────────

  async getPurchaseOrderAcknowledgedDocument(orderId: string): Promise<{
    id: number;
    originalFilename: string;
    fileExtension: string | null;
    mimetype: string | null;
    filesize: number | null;
    createdAt: string;
    uploadedByName: string | null;
  } | null> {
    const response = await axiosInstance.get(`${this.baseRoute}/${orderId}/acknowledged-document`);
    return response.data?.data ?? null;
  }

  async uploadPurchaseOrderAcknowledgedDocument(
    orderId: string,
    file: File
  ): Promise<{ success: boolean; data?: { id: number; originalFilename: string; createdAt: string } }> {    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosInstance.post(
      `${this.baseRoute}/${orderId}/acknowledged-document`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  }

  async deletePurchaseOrderAcknowledgedDocument(orderId: string): Promise<{ success: boolean }> {
    const response = await axiosInstance.delete(`${this.baseRoute}/${orderId}/acknowledged-document`);
    return response.data;
  }

  async fetchPurchaseOrderAcknowledgedDocument(
    orderId: string,
    mode: "preview" | "download"
  ): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get(
      `${this.baseRoute}/${orderId}/acknowledged-document/${mode}`,
      { responseType: "blob" }
    );
  }

  // ── PO Exports ──────────────────────────────────────────────────────────

  async listPOsForExport(params: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<{
    data: POExportListItem[];
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
  }> {
    const { page = 1, pageSize = 20, search } = params;
    const response = await axiosInstance.get(`/purchase/exports/list`, {
      params: {
        page,
        pageSize,
        ...(search && search.trim() ? { search: search.trim() } : {}),
      },
    });
    const payload = response.data ?? {};
    const rows: POExportListApiRow[] = Array.isArray(payload.data) ? payload.data : [];
    const data: POExportListItem[] = rows.map((row) => ({
      id: String(row.id),
      poNumber: row.po_number ?? null,
      poDate: row.date ?? null,
      partyName: row.party_name ?? null,
      createdByName: row.created_by_name ?? null,
      updatedByName: row.updated_by_name ?? null,
    }));
    return {
      data,
      pagination: payload.pagination ?? { page, pageSize, total: data.length, totalPages: 1 },
    };
  }

  async downloadPOWithoutPrice(orderId: string): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get(`/purchase/exports/${orderId}/without-price/download`, {
      responseType: "blob",
    });
  }

  async getPurchasePartySettings(): Promise<{ requireBeneficiaryPartyCode: boolean }> {
    const response = await axiosInstance.get<{ success: boolean; data: { requireBeneficiaryPartyCode: boolean } }>(
      `/purchase-order/settings/po-settings`
    );
    return response.data?.data ?? { requireBeneficiaryPartyCode: false };
  }

  async updatePurchasePartySettings(settings: { requireBeneficiaryPartyCode: boolean }): Promise<void> {
    await axiosInstance.put("/purchase-order/settings/po-settings", {
      requireBeneficiaryPartyCode: settings.requireBeneficiaryPartyCode,
    });
  }
}

// Dispatch-related type definitions
export interface ApprovedPurchaseOrder {
  id: number;
  poNumber: string;
  date: string | null;
  partyName: string | null;
  vendorName: string | null;
  projectCode: string | null;
  projectName: string | null;
  expenseCode: string | null;
  expenseDescription: string | null;
  deliveryAddress: string | null;
  createdAt: string | null;
}

export interface DispatchPurchaseOrderInfo {
  id: number;
  poNumber: string;
  date: string | null;
  deliveryAddress: string | null;
  projectCode: string | null;
  projectName: string | null;
  expenseCode: string | null;
  expenseDescription: string | null;
  partyName: string | null;
}

export interface DispatchPurchaseOrderItem {
  id: number;
  serialNo: number | null;
  itemName: string | null;
  description: string | null;
  hsnCode: string | null;
  workShipDate: string | null;
  uomId: number | null;
  unitName: string | null;
  quantity: number;
  dispatchedQuantity: number;
  remainingQuantity: number;
  unitRate: number;
  totalPrice: number;
  gstRate: number;
}

export interface CreateDispatchPayload {
  purchaseOrderId: number;
  dispatchDate?: string;
  expectedDeliveryDate?: string;
  transporter?: string;
  vehicleNumber?: string;
  trackingNumber?: string;
  notes?: string;
  items: {
    poItemId: number;
    quantity: number;
    expectedDeliveryDate?: string;
  }[];
}

export interface DispatchListItem {
  id: number;
  purchaseOrderId: number;
  poNumber: string;
  dispatchDate: string | null;
  expectedDeliveryDate: string | null;
  transporter: string | null;
  vehicleNumber: string | null;
  trackingNumber: string | null;
  notes: string | null;
  status: string;
  createdAt: string | null;
  createdByName: string | null;
  projectCode: string | null;
  expenseCode: string | null;
  partyName: string | null;
  itemCount: number;
}

export interface DispatchDetailItem {
  id: number;
  poItemId: number;
  serialNo: number | null;
  itemName: string | null;
  description: string | null;
  hsnCode: string | null;
  totalQuantity: number;
  dispatchedQuantity: number;
  receivedQuantity: number;
  expectedDeliveryDate: string | null;
  receivedDate: string | null;
  receptionNotes: string | null;
  status: string;
  uomId: number | null;
  unitName: string | null;
  unitRate: number;
}

export interface DispatchDetail {
  id: number;
  purchaseOrderId: number;
  poNumber: string;
  dispatchDate: string | null;
  expectedDeliveryDate: string | null;
  transporter: string | null;
  vehicleNumber: string | null;
  trackingNumber: string | null;
  notes: string | null;
  status: string;
  deliveryAddress: string | null;
  projectCode: string | null;
  projectName: string | null;
  expenseCode: string | null;
  expenseDescription: string | null;
  partyName: string | null;
  createdAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  items: DispatchDetailItem[];
}

export interface PendingDeliveryItem {
  id: number;
  purchaseOrderId: number;
  poNumber: string;
  dispatchDate: string | null;
  expectedDeliveryDate: string | null;
  transporter: string | null;
  vehicleNumber: string | null;
  trackingNumber: string | null;
  notes: string | null;
  status: string;
  deliveryAddress: string | null;
  projectCode: string | null;
  projectName: string | null;
  expenseCode: string | null;
  partyName: string | null;
  itemCount: number;
  totalQuantity: number;
  createdAt: string | null;
}

export interface MarkReceivedPayload {
  receivedDate?: string;
  items: {
    dispatchItemId: number;
    receivedQuantity: number;
    notes?: string;
  }[];
}

interface POExportListApiRow {
  id: string | number;
  po_number?: string | null;
  date?: string | null;
  party_name?: string | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
}

export interface POExportListItem {
  id: string;
  poNumber: string | null;
  poDate: string | null;
  partyName: string | null;
  createdByName: string | null;
  updatedByName: string | null;
}

const purchaseOrderService = new PurchaseOrderService();
export default purchaseOrderService;
